/* eslint-disable react-hooks/exhaustive-deps */
/*
 * Author M. Atoar Rahman
 * Title: D1 Form: Medicines Requisition for Facility
 * Description: Master Setup of D1 Form: Medicines Requisition for Facility Form: Create and Update
 * Date: 15/03/2022
 */
/* prettier-ignore */
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Col, Form, Row, Tab, Tabs } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import { Controller, useForm } from 'react-hook-form';
import Swal from 'sweetalert2';
import CommonApiService from '../../../services/CommonApiService';
import SweetAlertService from '../../../services/SweetAlertService';
import ToastifyService from '../../../services/ToastifyService';
import { checkMonthValidation_requisition } from '../../../Utils';
import { useD1Form } from './ContextAPI';
import useDraftService from './draftService';
import { clearMedicineCache } from './medicineListCache';
import { clearReq, getAllReq, loadReq } from './requisitionStore';
import RequisitionTable from './RequisitionTable';
import useMedicineList from './useMedicineList';

export default function FormInfo({ editData = null, d1Forms }) {
    // Dependancy
    const { drugCategory, getDrugCategory } = CommonApiService();
    const { submitConfirmSwal } = SweetAlertService();
    const { saveDraft, getDrafts, deleteDraft } = useDraftService();
    const getMedicineList = useMedicineList();
    const [errMsg, setErrMsg] = useState('');
    const { addD1Form, editD1Form, setShow } = useD1Form();

    // if true, disables months that already have a requisition form in the Month/Year picker.
    const DISABLE_TAKEN_MONTHS = true; // set to true to enable the feature

    // Local State
    const [page, setPage] = useState(0);
    const [pageLength, setPageLength] = useState(0);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState(() => new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear());
    const [draftVersion, setDraftVersion] = useState(0);
    const warmedKeyRef = useRef('');
    const draftLoadedRef = useRef(false);
    const disabledDates = useMemo(
        () =>
            (d1Forms || []).map(
                (f) => new Date(Number(f.year), Number(f.month) - 1, 1)
            ),
        [d1Forms]
    );

    // True when the selected Month/Year already has a prepared requisition
    // (create mode only — editData's own month must stay editable/loadable).
    const isMonthTaken = useMemo(
        () =>
            Boolean(
                !editData &&
                    selectedMonth &&
                    selectedYear &&
                    !checkMonthValidation_requisition(
                        d1Forms || [],
                        selectedMonth,
                        selectedYear
                    )
            ),
        [editData, d1Forms, selectedMonth, selectedYear]
    );

    // Clear the medicine-list cache when the form closes so each
    // "Create New" session fetches fresh data.
    useEffect(() => () => clearMedicineCache(), []);

    // Background warm-up: after category 1 (the active tab) resolves,
    // sequentially prefetch the rest so tab switches are instant. Re-runs
    // whenever the selected Month/Year changes so each month's lists are ready.
    useEffect(() => {
        if (!drugCategory?.length || isMonthTaken) {
            return undefined;
        }
        const key = `${selectedMonth}_${selectedYear}`;
        if (warmedKeyRef.current === key) {
            return undefined;
        }
        warmedKeyRef.current = key;
        let cancelled = false;
        const warmUp = () =>
            drugCategory.reduce(
                (chain, category) =>
                    chain.then(() =>
                        cancelled
                            ? undefined
                            : getMedicineList(category.id, {
                                  month: selectedMonth,
                                  year: selectedYear,
                              }).catch(() => undefined)
                    ),
                Promise.resolve()
            );
        warmUp();
        return () => {
            cancelled = true;
        };
    }, [drugCategory, selectedMonth, selectedYear, isMonthTaken, getMedicineList]);

    useEffect(() => {
        if (selectedMonth && selectedYear) {
            const checkMonth = checkMonthValidation_requisition(
                d1Forms || [],
                selectedMonth,
                selectedYear
            );
            if (!checkMonth) {
                setErrMsg('This month is already taken');
            } else {
                setErrMsg('');
            }
        }
    }, [selectedMonth, selectedYear, d1Forms]);

    const {
        formState: { errors },
        reset,
        handleSubmit,
        control,
        setValue,
        getValues,
    } = useForm({
        mode: 'onTouched',
        criteriaMode: 'firstError',
        reValidateMode: 'onBlur',
        defaultValues: useMemo(
            () => ({
                ...editData,
                month: (editData && new Date(editData.month)) || new Date(),
                year: (editData && new Date(editData.year)) || new Date(),
            }),
            [editData]
        ),
    });

    // Reset default values
    useEffect(() => {
        reset({
            ...editData,
            month: (editData && new Date(editData.month)) || new Date(),
            year: (editData && new Date(editData.year)) || new Date(),
        });
    }, [editData]);

    const dataList = useCallback(() => {
        getDrugCategory(); //  Drug Category
    }, []);

    useEffect(() => {
        dataList();
    }, [dataList]);

    useEffect(() => {
        setPageLength(drugCategory?.length);
    }, [drugCategory]);

    const handleClose = () => {
        setShow(false);
    };

    const continueDraft = (draft) => {
        const month = draft?.month;
        const year = draft?.year;
        if (month && year) {
            setSelectedMonth(month);
            setSelectedYear(year);
            setValue('month', new Date(year, month - 1, 1));
            setValue('year', new Date(year, 0, 1));
        }
        loadReq(draft?.requisition_data);
        draftLoadedRef.current = true;
        setDraftVersion((prev) => prev + 1);
    };

    const discardDraft = async (draft) => {
        if (draft?.id) {
            await deleteDraft(draft.id);
        }
        clearReq();
        draftLoadedRef.current = false;
        setDraftVersion((prev) => prev + 1);
        ToastifyService.updateNotify('Draft Discarded');
    };

    const openDraftPicker = (draftList) => {
        const inputOptions = {};
        draftList.forEach((draft) => {
            inputOptions[draft.id] = `${moment()
                .month(draft.month - 1)
                .format('MMMM')} ${draft.year} — saved ${moment(draft.updated_at).format(
                'DD MMM YYYY h:mm A'
            )}`;
        });

        Swal.fire({
            title: 'Select a Draft',
            input: 'select',
            inputOptions,
            inputPlaceholder: 'Choose a draft to continue',
            confirmButtonText: 'Continue',
            showCancelButton: true,
        }).then((result) => {
            if (result.value) {
                const draft = draftList.find(
                    (d) => String(d.id) === String(result.value)
                );
                if (draft) {
                    continueDraft(draft);
                }
            }
        });
    };

    const getDraftsForMonth = async (month, year) => {
        const draftList = (await getDrafts().catch(() => [])) || [];
        return draftList.filter(
            (draft) =>
                Number(draft.month) === Number(month) &&
                Number(draft.year) === Number(year)
        );
    };

    const showDraftFoundSwal = (draftList, onContinue, onDiscard, onCancel) => {
        const latest = draftList[0];
        const otherLink =
            draftList.length > 1
                ? `<br/><a href="#" id="other-drafts-link" style="font-size:0.9em;text-decoration:underline;">Other drafts (${draftList.length - 1})</a>`
                : '';
        Swal.fire({
            title: 'Draft Found',
            html: `A draft for <b>${moment()
                .month(latest.month - 1)
                .format('MMMM')} ${latest.year}</b> (saved on <b>${moment(
                latest.updated_at
            ).format('DD MMM YYYY h:mm A')}</b>) was found. Continue with this draft?${otherLink}`,
            icon: 'question',
            showDenyButton: true,
            showCancelButton: true,
            confirmButtonText: 'Continue',
            denyButtonText: 'Discard',
            cancelButtonText: 'Cancel',
            didOpen: () => {
                document
                    .getElementById('other-drafts-link')
                    ?.addEventListener('click', (e) => {
                        e.preventDefault();
                        Swal.close();
                        setTimeout(() => openDraftPicker(draftList), 50);
                    });
            },
        }).then((result) => {
            if (result.isConfirmed) {
                onContinue(latest);
            } else if (result.isDenied) {
                onDiscard(latest);
            } else if (result.dismiss) {
                onCancel?.();
            }
        });
    };

    // Draft restore prompt (create mode only)
    useEffect(() => {
        if (!editData) {
            clearReq();
            draftLoadedRef.current = false;
            let mounted = true;
            getDraftsForMonth(selectedMonth, selectedYear).then((matches) => {
                if (!mounted || !matches.length) {
                    return;
                }
                showDraftFoundSwal(matches, continueDraft, discardDraft);
            });
            return () => {
                mounted = false;
            };
        }
        return undefined;
    }, []);

    const handleSaveDraft = async () => {
        const month = getValues('month');
        const year = getValues('year');
        if (!month || !year) {
            ToastifyService.dbErrorNotify('Please select Month and Year first!');
            return;
        }
        const requisitions = getAllReq();
        if (!requisitions.length) {
            ToastifyService.dbErrorNotify('No requisition data to save yet!');
            return;
        }
        const response = await saveDraft({
            month: month.getMonth() + 1,
            year: year.getFullYear(),
            requisitions,
        });
        if (response?.status === 'success') {
            ToastifyService.saveNotify('Draft Saved Successfully');
        } else {
            ToastifyService.errorNotify(response?.msg || 'Draft Save Failed!');
        }
    };

    // Form Submit Handelar
    const formSubmitHandler = async (data) => {
        const isConfirmed = await submitConfirmSwal();

        if (!isConfirmed) {
            return;
        }

        const requisition = getAllReq();
        if (!requisition.length) {
            ToastifyService.dbErrorNotify('Please fill the requisition data first!');
            return;
        }

        setIsSubmitted(true);
        const formData = {
            ...data,
            month: data.month.getMonth() + 1,
            year: data.year.getFullYear(),
            requisition,
        };
        console.log('payload', formData);

        if (editData) {
            editD1Form(formData, editData.id); // Update Data
        } else {
            addD1Form(formData); // Create Data
        }
        reset();
    };

    const confirmMonthYearChange = async (nextMonth, nextYear, applyChange, revert) => {
        if (nextMonth === selectedMonth && nextYear === selectedYear) {
            return;
        }
        const proceed = () => {
            setSelectedMonth(nextMonth);
            setSelectedYear(nextYear);
            setDraftVersion((prev) => prev + 1);
            applyChange();
        };
        const changeWithReset = () => {
            clearReq();
            draftLoadedRef.current = false;
            proceed();
        };

        if (editData) {
            if (getAllReq().length) {
                Swal.fire({
                    title: 'Keep or Clear Requisition Data?',
                    html: 'You have entered requisition data. Do you want to keep it with the new Month/Year or clear it?',
                    icon: 'question',
                    showDenyButton: true,
                    showCancelButton: true,
                    confirmButtonText: 'Keep',
                    denyButtonText: 'Reset/Clear',
                    cancelButtonText: 'Cancel',
                }).then((result) => {
                    if (result.isConfirmed) {
                        proceed();
                    } else if (result.isDenied) {
                        changeWithReset();
                    } else if (result.dismiss) {
                        revert();
                    }
                });
            } else {
                proceed();
            }
            return;
        }

        const matches = await getDraftsForMonth(nextMonth, nextYear);
        const latest = matches[0];
        const hasCurrentData = draftLoadedRef.current || getAllReq().length > 0;

        if (latest) {
            if (hasCurrentData) {
                Swal.fire({
                    title: 'Draft Found',
                    html: `A draft for <b>${moment()
                        .month(latest.month - 1)
                        .format('MMMM')} ${latest.year}</b> exists. What do you want to keep?`,
                    icon: 'question',
                    showDenyButton: true,
                    showCancelButton: true,
                    confirmButtonText: 'Keep New Data',
                    denyButtonText: 'Keep Draft Data',
                    cancelButtonText: 'Cancel',
                }).then((result) => {
                    if (result.isConfirmed) {
                        proceed();
                    } else if (result.isDenied) {
                        continueDraft(latest);
                    } else if (result.dismiss) {
                        revert();
                    }
                });
            } else {
                showDraftFoundSwal(
                    matches,
                    continueDraft,
                    (draft) => {
                        proceed();
                        discardDraft(draft);
                    },
                    revert
                );
            }
            return;
        }

        if (draftLoadedRef.current) {
            Swal.fire({
                title: 'Discard Draft Data?',
                html: 'Changing the Month/Year will discard the draft data loaded for this form.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'OK',
                cancelButtonText: 'Cancel',
            }).then((result) => {
                if (result.isConfirmed) {
                    changeWithReset();
                } else if (result.dismiss) {
                    revert();
                }
            });
            return;
        }
        if (getAllReq().length) {
            Swal.fire({
                title: 'Keep or Clear Requisition Data?',
                html: 'You have entered requisition data. Do you want to keep it with the new Month/Year or clear it?',
                icon: 'question',
                showDenyButton: true,
                showCancelButton: true,
                confirmButtonText: 'Keep',
                denyButtonText: 'Reset/Clear',
                cancelButtonText: 'Cancel',
            }).then((result) => {
                if (result.isConfirmed) {
                    proceed();
                } else if (result.isDenied) {
                    changeWithReset();
                } else if (result.dismiss) {
                    revert();
                }
            });
            return;
        }
        proceed();
    };

    const tableDataDisplay = useCallback(
        () =>
            drugCategory?.map((item, index) => (
                <Tab key={item.id} eventKey={index} title={item.name}>
                    <RequisitionTable
                        key={`${item.id}-${draftVersion}`}
                        id={item.id}
                        month={selectedMonth}
                        year={selectedYear}
                    />
                </Tab>
            )),
        [drugCategory, draftVersion, selectedMonth, selectedYear]
    );

    return (
        <Form>
            <fieldset className="customFieldset p-4 pb-2 mb-3">
                <Row>
                    <Col lg={6}>
                        <Row>
                            <Col lg={6}>
                                <Form.Group className="mb-3" controlId="formGrnStockID">
                                    <Row className="align-items-center">
                                        <Col lg={4}>
                                            <Form.Label>
                                                Month<span className="invalid">*</span>
                                            </Form.Label>
                                        </Col>
                                        <Col lg={8}>
                                            <Controller
                                                control={control}
                                                name="month"
                                                render={({ field }) => (
                                                    <DatePicker
                                                        {...field}
                                                        closeOnScroll
                                                        selected={field.value}
                                                        placeholderText="Select month"
                                                        dateFormat="MMMM"
                                                        showMonthYearPicker
                                                        showFullMonthYearPicker
                                                        {...(DISABLE_TAKEN_MONTHS && { excludeDates: disabledDates })}
                                                        onChange={(date) => {
                                                            const month = date.getMonth() + 1;
                                                            const prevMonth = selectedMonth;
                                                            const prevYear = selectedYear;
                                                            confirmMonthYearChange(
                                                                month,
                                                                selectedYear,
                                                                () => field.onChange(date),
                                                                () =>
                                                                    field.onChange(
                                                                        new Date(prevYear, prevMonth - 1, 1)
                                                                    )
                                                            );
                                                        }}
                                                        className="form-control"
                                                        popperPlacement="top-end"
                                                    />
                                                )}
                                                rules={{
                                                    required: 'Month is required.',
                                                    valueAsDate: true,
                                                }}
                                            />
                                            {errMsg && <span className="invalid">{errMsg}</span>}
                                        </Col>
                                    </Row>
                                </Form.Group>
                            </Col>
                            <Col lg={6}>
                                <Form.Group className="mb-3" controlId="formGrnStockID">
                                    <Row className="align-items-center">
                                        <Col lg={4}>
                                            <Form.Label>
                                                Year<span className="invalid">*</span>
                                            </Form.Label>
                                        </Col>

                                        <Col lg={8}>
                                            <Controller
                                                control={control}
                                                name="year"
                                                render={({ field }) => (
                                                    <DatePicker
                                                        {...field}
                                                        closeOnScroll
                                                        selected={field.value}
                                                        dateFormat="yyyy"
                                                        showYearPicker
                                                        placeholderText="Select year"
                                                        onChange={(date) => {
                                                            const year = date?.getFullYear();
                                                            const prevMonth = selectedMonth;
                                                            const prevYear = selectedYear;
                                                            confirmMonthYearChange(
                                                                selectedMonth,
                                                                year,
                                                                () => field.onChange(date),
                                                                () =>
                                                                    field.onChange(
                                                                        new Date(prevYear, prevMonth - 1, 1)
                                                                    )
                                                            );
                                                        }}
                                                        className="form-control"
                                                        popperPlacement="top-end"
                                                    />
                                                )}
                                                rules={{
                                                    required: 'Year is required.',
                                                    valueAsDate: true,
                                                }}
                                            />

                                            {errors.year && (
                                                <span className="invalid">
                                                    {errors.year?.message}
                                                </span>
                                            )}
                                        </Col>
                                    </Row>
                                </Form.Group>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </fieldset>

            {isMonthTaken ? (
                <div className="alert alert-warning" role="alert">
                    {moment().month(selectedMonth - 1).format('MMMM')}{' '}
                    {selectedYear} requisition is already prepared.
                </div>
            ) : (
                <Tabs
                    activeKey={page}
                    transition={false}
                    id="noanim-tab-example"
                    className="mb-3"
                    mountOnEnter
                    // eslint-disable-next-line radix
                    onSelect={(k) => setPage(parseInt(k))}
                >
                    {tableDataDisplay()}
                </Tabs>
            )}

            <div className="formFooter">
                {!editData && (
                    <Button
                        className="me-2"
                        variant="warning"
                        style={{ minWidth: '6rem' }}
                        onClick={handleSaveDraft}
                    >
                        Save Draft
                    </Button>
                )}
                <Button className="btnCancel" variant="secondary" onClick={handleClose}>
                    Close
                </Button>
                <Button
                    className={`btnInfo me-2 ms-2 ${page === 0 && 'd-none'}`}
                    variant="primary"
                    style={{ minWidth: '6rem' }}
                    disabled={page === 0}
                    onClick={() => {
                        setPage((currPage) => currPage - 1);
                    }}
                >
                    Prev
                </Button>
                <Button
                    type="button"
                    className={`btnSuccess ${page === pageLength - 1 && 'd-none'}`}
                    variant="success"
                    style={{ minWidth: '6rem' }}
                    onClick={() => setPage((currPage) => currPage + 1)}
                >
                    Next
                </Button>

                <Button
                    onClick={handleSubmit(formSubmitHandler)}
                    className={`btnSuccess ${page !== pageLength - 1 && 'd-none'}`}
                    type="button"
                    variant="success"
                    style={{ minWidth: '6rem' }}
                    disabled={isSubmitted}
                >
                    {isSubmitted ? `Loading...` : `Submit`}
                </Button>
            </div>
        </Form>
    );
}
